import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { NgbCarousel } from '@ng-bootstrap/ng-bootstrap';
import { Availability, Customer, CustomerProductPrice, GungFlow, Product, ProductDetailComponent, SelectedCustomerService } from 'gung-standard';
import { GungUser as User } from 'gung-standard';
import { first } from 'rxjs';
import { HlDisplayMetadataHelperService } from '../../services/hl-display-metadata-helper.service';
import { HlDisplayPortalFeaturesService, PriceSettingsGroup } from '../../services/hl-display-portal-features.service';
import { HlProductInputQuantityConfigService } from '../../services/hl-product-input-quantity-config.service';
import { HlProductService } from '../../services/hl-product.service';

@Component({
  selector: 'app-hl-display-product-detail',
  templateUrl: './hl-display-product-detail.component.html',
  styleUrls: ['./hl-display-product-detail.component.scss'],
  standalone: false
})
export class HlDisplayProductDetailComponent extends ProductDetailComponent implements OnInit {
  protected productService = inject(HlProductService);
  protected customerService = inject(SelectedCustomerService);
  protected hldisplayPortalFeaturesService = inject(HlDisplayPortalFeaturesService);
  protected hlDisplayMetadataHelperService = inject(HlDisplayMetadataHelperService);
  protected quantityConfigService = inject(HlProductInputQuantityConfigService);

  public displayStaffedPrices: boolean;
  public displayDiscountFromPriceList: boolean;
  public currencyCode: string;

  public arsForMainWarehouse: {};

  @ViewChild(NgbCarousel)
  private myCarousel: NgbCarousel;
  @ViewChild('scrollContainer')
  scrollingImages: ElementRef;

  public imageIdString = 'productImage';
  private customerId: string;
  private selectedCustomer: Customer;

  public isCustomerService = false;

  currentLang = this.translateService.currentLang;

  baseCostCurrency: string = '';
  protected hidePrices: boolean = false;

  ngOnInit(): void {
    super.ngOnInit();

    this.customerService.getSelectedCustomer().subscribe(cust => {
      this.customerId = cust.id;
      this.selectedCustomer = cust;
    });
    this.hldisplayPortalFeaturesService
      .getPortalFeatureGroup('priceSettingsGroup')
      .pipe(first())
      .subscribe(priceSettingsGroup => {
        this.displayStaffedPrices = (priceSettingsGroup as PriceSettingsGroup).displayStaffedPrices;
        this.baseCostCurrency = (priceSettingsGroup as PriceSettingsGroup)?.baseCostCurrency || '';
        this.displayDiscountFromPriceList = (priceSettingsGroup as PriceSettingsGroup).displayDiscountFromPriceList;
        this.hidePrices = (priceSettingsGroup as PriceSettingsGroup)?.hidePrices;
      });
  }

  protected performStandardSetup(
    product: Product,
    availability: Availability,
    price: CustomerProductPrice,
    user: User,
    flow: GungFlow
  ): void {
    super.performStandardSetup(product, availability, price, user, flow);
    this.quantityConfigService.getStepAmount(product.id, product, price);
    this.quantityConfigService.getMinimumOrderQuantity(product.id, product, price);
    this.isCustomerService = user.roles.filter(role => role.toUpperCase() === 'CUSTOMER_SERVICE').length > 0;
    this.arsForMainWarehouse = product.extra.ars?.find(ars => ars.lagstalle === '0');

    if (availability.extra._componentsAvailable) {
      const componentIds = Object.keys(availability.extra._componentsAvailable);
      componentIds.forEach(id => {
        this.componentAmounts[id] = availability.extra._componentsRequired[id];
      });

      this.productService
        .getComponentsByIds(componentIds)
        .pipe(first())
        .subscribe(products => (this.components = products));
    } else {
      this.components = [];
    }

    if (this.product.extra.conceptId) {
      this.productService.getProduct(this.product.extra.conceptId).subscribe(concept => {
        this.concept = concept;
      });
    }
  }

  select(index: number): void {
    this.myCarousel.select(this.imageIdString + index);
  }

  scrollImagesRight(): void {
    const scroller = this.scrollingImages.nativeElement as HTMLElement;
    const scrollWidth = Math.min(
      scroller.scrollLeft + scroller.clientWidth,
      scroller.scrollWidth - scroller.clientWidth
    );
    scroller.scroll({ left: scrollWidth, top: 0, behavior: 'smooth' });
  }

  scrollImagesLeft(): void {
    const scroller = this.scrollingImages.nativeElement as HTMLElement;
    const scrollWidth = Math.max(scroller.scrollLeft - scroller.clientWidth, 0);
    scroller.scroll({ left: scrollWidth, top: 0, behavior: 'smooth' });
  }

  getColorTranslated(name: string) {
    let metadataTableList = this.metadataService.getMetadataTableList('ColourName');

    const finalMetadata = metadataTableList.filter(element => {
      if (Array.isArray(name)) {
        return name.includes(element.Id);
      }
      return element.Id === name;
    });
    if (finalMetadata?.length > 0) {
      return (finalMetadata as any).map(colorTranslated => colorTranslated.Values[this.currentLang]);
    }
    return '';
  }

  /* Deprecated

  getName(): string {
    if (!this.isSales) {
      return this.product.name;
    }

    const id = this.product.id;

    // const itemGroup = this.metadataService.getMetadataValue('vg', 'varugruppbeskr', this.product.extra.ar.varugruppkod);
    const itemProductSystem = this.product.extra.hlPimProduct?.ProductSystem;
    let itemDesc = this.product.extra.ar.artbeskr;
    return `${id}${itemProductSystem ? ' - ' + itemProductSystem : ''} - ${itemDesc}`;
  }*/

  getKuarDesc(): string {
    const hasKuar = !!this.selectedCustomer.extra.kuarMapped?.[this.product.id];
    if (!hasKuar) {
      return '';
    }

    const artbeskr2 = this.selectedCustomer.extra.kuarMapped?.[this.product.id]?.artbeskr2 || '';
    const artnrkund = this.selectedCustomer.extra.kuarMapped?.[this.product.id]?.artnrkund || '';
    return `${artbeskr2} (${artnrkund})`;
  }

  /*
  getDescription(): string {

    if (!this.isSales) {
      return this.product.description;
    }

    let desc1 = '';
    const desc2 = this.product.extra.ar.artbeskrspec;
    return `${desc1} (${desc2})`;
  }*/
}
